import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Spinner,
  Center,
  TableContainer,
  useToast,
} from '@chakra-ui/react';
import supabase from '../supabaseClient';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return String(value);
  }
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return String(value);
  }
};

const formatText = (value) => (value?.trim?.() ? value.trim() : '—');

const formatList = (values) => (values?.length ? values.join(', ') : '—');

const formatYesNo = (value) => {
  if (!value) return '—';
  if (value === 'yes') return 'Yes';
  if (value === 'no') return 'No';
  return value;
};

const WaitlistManagement = ({ isVisible = true }) => {
  const [registrations, setRegistrations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    if (!isVisible) return;

    const fetchRegistrations = async () => {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('registrations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        toast({
          title: 'Error fetching waitlist',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setRegistrations([]);
      } else {
        setRegistrations(data || []);
      }
      setIsLoading(false);
    };

    fetchRegistrations();
  }, [isVisible, toast]);

  if (isLoading) {
    return (
      <Center height="400px">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Box>
      <Heading mb={2}>Waitlist</Heading>
      <Text color="gray.600" mb={6}>
        {registrations.length} registration{registrations.length === 1 ? '' : 's'} from the public waitlist form.
      </Text>

      {registrations.length === 0 ? (
        <Text color="gray.500">No waitlist registrations yet.</Text>
      ) : (
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th whiteSpace="nowrap">Added</Th>
                <Th whiteSpace="nowrap">Patient Name</Th>
                <Th whiteSpace="nowrap">Age</Th>
                <Th whiteSpace="nowrap">Birthday</Th>
                <Th whiteSpace="nowrap">Parent / Guardian</Th>
                <Th whiteSpace="nowrap">Cellphone</Th>
                <Th whiteSpace="nowrap">City</Th>
                <Th whiteSpace="nowrap">Services</Th>
                <Th whiteSpace="nowrap">Consulted</Th>
                <Th whiteSpace="nowrap">Pediatrician</Th>
                <Th whiteSpace="nowrap">Diagnosis</Th>
                <Th whiteSpace="nowrap">PWD Cardholder</Th>
              </Tr>
            </Thead>
            <Tbody>
              {registrations.map((registration) => (
                <Tr key={registration.id}>
                  <Td whiteSpace="nowrap">{formatDateTime(registration.created_at)}</Td>
                  <Td whiteSpace="nowrap">{formatText(registration.name_of_patient)}</Td>
                  <Td whiteSpace="nowrap">{formatText(registration.age)}</Td>
                  <Td whiteSpace="nowrap">{formatDate(registration.birthday)}</Td>
                  <Td whiteSpace="nowrap">{formatText(registration.name_of_parent_guardian)}</Td>
                  <Td whiteSpace="nowrap">{formatText(registration.cellphone_number)}</Td>
                  <Td whiteSpace="nowrap">{formatText(registration.city)}</Td>
                  <Td minW="120px">{formatList(registration.services)}</Td>
                  <Td whiteSpace="nowrap">{formatYesNo(registration.consulted)}</Td>
                  <Td minW="140px">
                    {registration.consulted === 'yes'
                      ? formatText(registration.name_of_pediatrician)
                      : '—'}
                  </Td>
                  <Td minW="180px" whiteSpace="normal">
                    {registration.consulted === 'yes'
                      ? formatText(registration.diagnosis)
                      : '—'}
                  </Td>
                  <Td whiteSpace="nowrap">{formatYesNo(registration.pwd_cardholder)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default WaitlistManagement;
